import { axiosInstance } from "./index";

// A read-only "standard guideline" section returned by the backend (music, safe
// zones, sending for approval). Rendered but not editable; not persisted here.
export interface BriefGuidelineSection {
	title: string;
	body: string;
	links: string[];
}

// The editable campaign brief. Dates are ISO date strings ("YYYY-MM-DD") or null.
export interface CampaignBrief {
	campaignId: string;

	// Timeline
	conceptDueDate: string | null;
	videoAssetsDueDate: string | null;
	goLiveDate: string | null;

	// Brief narrative
	about: string | null;
	objective: string | null;
	keyMessages: string | null;
	creativeDirection: string | null;

	// Video format
	videoFormatRequiredElement: string | null;
	videoFormatSuggestedFormats: string | null;

	// Guidelines & guardrails
	editGuidelines: string | null;
	campaignDos: string[];
	campaignDonts: string[];

	// Call to action & tagging
	callToAction: string | null;
	hashtags: string[];

	createdAt?: string;
	lastModifiedAt?: string;
}

// GET response: the brief plus the read-only standard guidelines.
export interface CampaignBriefResponse extends CampaignBrief {
	standardGuidelines: BriefGuidelineSection[];
}

// Fields the client submits on save (campaignId is in the URL; metadata and
// standardGuidelines are ignored by the backend).
export type CampaignBriefInput = Omit<CampaignBrief, "campaignId" | "createdAt" | "lastModifiedAt">;

export const campaignBriefAPI = {
	// Fetch a campaign's brief. Backend returns an empty brief (not 404) when none
	// exists yet, always including standardGuidelines.
	get: async (campaignId: string): Promise<CampaignBriefResponse> => {
		try {
			const response = await axiosInstance.get<CampaignBriefResponse>(`/campaigns/${campaignId}/brief`);
			return response.data;
		} catch (error) {
			console.error(`Error fetching campaign brief for ${campaignId}:`, error);
			throw new Error("Failed to fetch campaign brief");
		}
	},

	// Insert-or-update the brief. Backend returns { message, brief }.
	update: async (campaignId: string, input: CampaignBriefInput): Promise<CampaignBriefResponse> => {
		try {
			const response = await axiosInstance.put<{ message: string; brief: CampaignBriefResponse }>(
				`/campaigns/${campaignId}/brief`,
				input,
			);
			return response.data.brief;
		} catch (error) {
			console.error(`Error updating campaign brief for ${campaignId}:`, error);
			throw new Error("Failed to update campaign brief");
		}
	},
};
